import { PlanCompleteSummary } from '@client/components/ltc-financial-planning/PlanCompleteSummary';
import { Button } from '@client/components/ui/button';
import { useLtcPlanComparison } from '@client/hooks/useLtcPlanComparison';
import { useNavigate, useParams } from 'react-router-dom';

import { StepContentProps } from '../types';

export function CompleteStep({ navigation }: StepContentProps) {
  const navigate = useNavigate();
  const { clientId } = useParams<{ clientId: string | undefined }>();

  const {
    projectedLtcCost,
    selfFundingCapacity,
    policyCoverage,
    chosenPolicy,
    priorityAssets,
    comparison,
  } = useLtcPlanComparison();

  if (!clientId) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-50">
        <div className="text-center">
          <h2 className="mb-2 text-xl font-semibold text-gray-900">Client ID not found</h2>
          <p className="text-gray-600">Unable to load the financial planning page.</p>
        </div>
      </div>
    );
  }

  const handleStartOver = () => {
    navigate(`/clients/${clientId}/onboarding/ltc-financial-planning?step=assets`);
  };

  const handleGoToDashboard = () => {
    navigate(`/clients/${clientId}`);
  };

  return (
    <div className="w-full py-8">
      <PlanCompleteSummary
        projectedLtcCost={projectedLtcCost}
        selfFundingCapacity={selfFundingCapacity}
        remainingGap={comparison.withoutInsurance.gap}
        selectedPolicy={{
          name: chosenPolicy?.productName || 'Selected Policy',
          coverage: policyCoverage,
        }}
        updatedGap={comparison.withInsurance.gap}
        updatedSurplus={comparison.withInsurance.surplus}
        accountsProtected={priorityAssets.length}
        className="mx-auto max-w-3xl"
      />

      <div className="mx-auto mt-8 flex max-w-lg flex-col gap-3">
        <Button
          onClick={handleGoToDashboard}
          className="w-full"
        >
          Start Application
        </Button>
        <Button
          variant="outline"
          onClick={handleStartOver}
          className="w-full"
        >
          Create Another Plan
        </Button>
      </div>

      <div className="mx-auto mt-8 max-w-lg rounded-xl border border-gray-200 bg-white p-5">
        <h3 className="mb-3 font-semibold text-gray-900">What happens next?</h3>
        <ol className="space-y-3">
          <li className="flex gap-3">
            <span className="bg-primary flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-xs font-semibold text-white">
              1
            </span>
            <span className="text-sm text-gray-600">
              A licensed advisor will review your plan and reach out within 24 hours.
            </span>
          </li>
          <li className="flex gap-3">
            <span className="bg-primary flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-xs font-semibold text-white">
              2
            </span>
            <span className="text-sm text-gray-600">
              They'll answer any questions and help you finalize your application.
            </span>
          </li>
          <li className="flex gap-3">
            <span className="bg-primary flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-xs font-semibold text-white">
              3
            </span>
            <span className="text-sm text-gray-600">
              Once approved, your protection plan goes into effect!
            </span>
          </li>
        </ol>
      </div>
    </div>
  );
}
